import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import WithColor from 'util/hoc/with-color';
import styled from 'styled-components';
import withConfig from 'util/hoc/with-config';
import { resolve } from 'library/helpers/path-resolver';
import WindowResizeable from 'library/hoc/on-window-resize';

const LogoContainer = styled.div`
  color: ${(props) => props.colors.secondary};
  font-weight: 800;
  font-size: 1.25em;
  display: flex;
  align-items: center;
  ${(props) => !props.noBorder && 'padding: 1rem;'}
  ${(props) =>
    !props.noBorder && 'border-right: 1px solid #d8d8d8;'}
  height: 100%;
  img {
    height: 45px !important;
    width: auto !important;
  }
  @media (max-width: 1024px) {
    > :not(svg) {
      display: ${(props) => (props.showLogo ? 'unset' : 'none')};
    }
  }
`;

const LogoHighlight = styled.span`
  color: ${(props) => props.colors.tertiary};
  margin: 0 0.125em;
`;
const LogoText = styled.div`
  white-space: nowrap;
`;

const LogoSvg = styled.svg`
  margin-right: 0.5em;
`;

const Logo = ({
  width = '2.25em',
  height = '2.25em',
  colors = {},
  configuration,
  windowWidth = window.innerWidth,
  noBorder,
  showLogo,
  mobileLogo,
}) => (
  <LogoContainer noBorder={noBorder} colors={colors} showLogo={showLogo}>
    {resolve(colors, 'logo', false) ? (
      <img
        src={
          (windowWidth < 1024 || mobileLogo) &&
          resolve(colors, 'logo_mobile', false)
            ? colors.logo_mobile
            : colors.logo
        }
        alt="Logo"
      />
    ) : (
      <Fragment>
        <LogoSvg
          width={width}
          height={height}
          viewBox="0 0 40 40"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <path
              d="M32.3200501,14 C29.4577461,11.1581459 24.8472984,12.0316973 22,9.18984322 L24.892315,3 C27.5520444,5.64314928 31.7160777,5.06952965 34.6008899,7.26653033 C34.9769263,7.54847461 35.1050655,8.05447931 34.9085032,8.48125426 L32.3200501,14 Z"
              fill={colors.journey_logo_color_1}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M17,33.5614516 C21.1143828,32.4986547 22.7434881,28.0552593 26.8737259,27 L31,32.6267934 C27.1710079,33.6142146 25.4983012,37.5262116 21.9705549,38.9206614 C21.513092,39.1039334 20.9831289,38.9615285 20.6942242,38.5777021 L17,33.5614516 Z"
              fill={colors.journey_logo_color_1}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M8.68119739,11 C7.55349129,14.9208294 10.8915014,18.4797361 9.76379526,22.3967955 L2.32093495,23 C3.37072682,19.3506126 0.553511928,16.0179076 1.06200486,12.4137606 C1.12371369,11.9442287 1.53386132,11.5781569 2.04618474,11.535344 L8.68119739,11 Z"
              fill={colors.journey_logo_color_1}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M26,6.90548147 C21.905,6.55341213 18.8882353,10.3322897 14.7932353,9.97630851 L13,3.09139693 C16.7967647,3.41999499 19.6720588,0.173133276 23.3541176,0.00101048632 C23.8365475,-0.0205644115 24.2682303,0.305382407 24.3902941,0.783386802 L26,6.90548147 Z"
              fill={colors.journey_logo_color_2}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M27,32.3446263 C29.4116261,28.928083 27.6716418,24.4165433 30.0832679,21 L37,22.8262836 C34.7572663,26.0029338 36.1044776,30.1314181 34.3605656,33.4396226 C34.1408546,33.8648945 33.6523804,34.0846428 33.1822467,33.9697092 L27,32.3446263 Z"
              fill={colors.journey_logo_color_2}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M4.72463712,21 C6.49076769,24.6823872 11.2377256,25.4393013 13,29.1216885 L8.0332398,34 C6.390507,30.571179 2.16798959,29.6780204 0.166632028,26.6049491 C-0.0976232754,26.2083231 -0.0429424564,25.6842963 0.297742158,25.3484716 L4.72463712,21 Z"
              fill={colors.journey_logo_color_2}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M33.4193548,24.4334783 C33.0322581,20.2426087 28.8322581,17.8595652 28.4529032,13.6686957 L34.8477419,10.6556522 C35.2,14.5686957 38.8696774,16.8891304 39.6825806,20.5673913 C39.7890117,21.0441562 39.5542445,21.5317979 39.1174194,21.7413043 L33.4193548,24.4334783 Z"
              fill={colors.journey_logo_color_3}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M16.7741935,6.03826087 C13.3754839,8.46434783 13.4412903,13.3478261 10.0387097,15.766087 L4.25935484,11.6769565 C7.42580645,9.41913043 7.5883871,5.02478261 10.3367742,2.48913043 C10.690155,2.15397973 11.226884,2.11324677 11.6258065,2.39130435 L16.7741935,6.03826087 Z"
              fill={colors.journey_logo_color_3}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
            <path
              d="M9.34967742,29.7826087 C13.1277419,31.5473913 17.2735484,29.0508696 21.0554839,30.8156522 L20.44,37.9334783 C16.9251613,36.29 13.0851613,38.3247826 9.52774194,37.2056522 C9.0646007,37.0633865 8.76286742,36.6132069 8.80387097,36.1256522 L9.34967742,29.7826087 Z"
              fill={colors.journey_logo_color_3}
              fillRule="nonzero"
              style={{ mixBlendMode: 'multiple' }}
            ></path>
          </g>
        </LogoSvg>
      </Fragment>
    )}
  </LogoContainer>
);

Logo.propTypes = {
  colors: PropTypes.object,
  width: PropTypes.any,
  height: PropTypes.any,
  configuration: PropTypes.object,
  windowWidth: PropTypes.number,
  noBorder: PropTypes.bool,
  showLogo: PropTypes.bool,
  mobileLogo: PropTypes.bool,
};

export default WindowResizeable(WithColor(withConfig(Logo)));
